import { TEST_IDS } from '@va/constants';
import { useLocalizationContext } from '@va/localization';
import { ChartContext } from '@va/types/charts';
import { Chart, ChartConfiguration, ChartData } from 'chart.js';
import classNames from 'classnames';

import { assign, isNil } from 'lodash';
import { CSSProperties, memo, ReactNode, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { buildIndicatorAnnotations } from '../indicatorAnnotations';
import '../registerChartPlugins';
import {
  buildTicks,
  computeMinMaxValues,
  defaultYAxisTickCallback,
  hasMaxPoint,
  hasMinMaxPoint,
  hasMinPoint,
  limitTicksCallback,
  MinMaxValues,
} from './utils';

declare module 'chart.js' {
  interface Chart {
    $showDataLabels?: boolean;
    $highlightMinMax?: boolean;
    $minMaxValues?: MinMaxValues[];
  }
}

export type LineChartConfigOptions = {
  limitTicks?: boolean;
  tickFontSize?: number;
  stacked?: boolean;
  yAxisTickCallback?: (value: number | string) => string;
  hideAllScales?: boolean;
  showTrendLine?: boolean;
  showDataLabels?: boolean;
  highlightMinMax?: boolean;
  datalabelsValueFormatter?: (val: any) => any;
};

export type LineChartProps = {
  id: string;
  chartData: ChartData<'line'>;
  wrapperStyle?: CSSProperties;
  tooltipFn?: (tooltipCtx: ChartContext) => ReactNode;
  className?: string;
  configOptions?: LineChartConfigOptions;
  isLoading?: boolean;
};

export const LineChart = memo(
  ({ id, chartData, wrapperStyle, tooltipFn, className, configOptions = {}, isLoading }: LineChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<Chart<'line'>>(null);
    const minMaxValuesRef = useRef(computeMinMaxValues(chartData.datasets));
    const { locale, translate } = useLocalizationContext();
    const { limitTicks, tickFontSize, yAxisTickCallback, datalabelsValueFormatter } = configOptions;
    const [tooltipContext, setTooltipContext] = useState<ChartContext | undefined>();

    useEffect(() => {
      minMaxValuesRef.current = computeMinMaxValues(chartData.datasets);
    }, [chartData]);

    useEffect(() => {
      if (!canvasRef.current || !containerRef.current) return;
      const canvasContext = canvasRef.current.getContext('2d');
      if (!canvasContext) return;

      const hasSingleLabel = chartData?.datasets[0]?.data?.length === 1;

      const minMaxValues = configOptions.highlightMinMax ? minMaxValuesRef.current : null;

      const datasets = chartData.datasets.map((dataset) => ({
        ...dataset,
        trendlineLinear: configOptions.showTrendLine
          ? {
              colorMin: dataset.borderColor as string,
              colorMax: dataset.borderColor as string,
              lineStyle: 'dotted' as const,
              width: 1,
            }
          : undefined,
      }));

      const config: ChartConfiguration<'line'> = {
        data: { ...chartData, datasets, labels: [...chartData.labels!] },
        type: 'line',
        options: {
          locale: locale,
          maintainAspectRatio: false,
          responsive: true,
          animation: false,
          onResize: () => {
            if (chartRef.current) {
              setTooltipContext({ chart: chartRef.current, tooltip: chartRef.current.tooltip! });
            }
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            annotation: {
              annotations: buildIndicatorAnnotations(chartRef.current),
            },
            datalabels: {
              display: (ctx) => {
                if (hasMinMaxPoint(ctx, minMaxValues))
                  return ctx.chart.tooltip?.dataPoints?.[0]?.dataIndex === ctx.dataIndex ? true : 'auto';
                return configOptions.showDataLabels ? 'auto' : false;
              },
              align: 'top',
              offset: -4,
              backgroundColor: (ctx) =>
                hasMinMaxPoint(ctx, minMaxValues) ? (ctx.dataset.borderColor as string) : null,
              borderRadius: (ctx) => (hasMinMaxPoint(ctx, minMaxValues) ? 10 : 0),
              color: (ctx) => (hasMinMaxPoint(ctx, minMaxValues) ? '#ffffff' : (ctx.dataset.borderColor as string)),
              formatter: (value, ctx) => {
                const formatted = datalabelsValueFormatter?.(value) ?? value;
                if (minMaxValues !== null) {
                  if (hasMaxPoint(ctx)) return translate('card.linechart.label.maxValue', { value: formatted });
                  if (hasMinPoint(ctx)) return translate('card.linechart.label.minValue', { value: formatted });
                }
                return formatted;
              },
              font: { size: 11 },
              padding: (ctx) => (hasMinMaxPoint(ctx, minMaxValues) ? { top: 2, right: 5, bottom: 2, left: 5 } : 2),
              textStrokeWidth: (ctx) => (hasMinMaxPoint(ctx, minMaxValues) ? 0 : 3),
              textStrokeColor: 'white',
            },
            legend: {
              display: false,
            },
            title: {
              display: false,
            },
            tooltip: {
              mode: 'index',
              position: 'average',
              enabled: !tooltipFn,
              external: (context) => setTooltipContext(context),
            },
          },
          scales: {
            x: {
              alignToPixels: true,
              display: !configOptions?.hideAllScales,
              offset: true,
              ticks: {
                ...buildTicks('#696969', tickFontSize),
                maxRotation: 0,
                autoSkip: true,
                major: {
                  enabled: true,
                },
              },
              afterBuildTicks: (axis) => limitTicksCallback(axis, limitTicks),
            },
            y: {
              alignToPixels: true,
              display: !configOptions?.hideAllScales,
              stacked: configOptions?.stacked,
              type: 'linear',
              min: 0,
              border: {
                display: false,
              },
              grid: {
                color: 'transparent',
              },
              position: 'left',
              ticks: {
                ...buildTicks('#969696', tickFontSize),
                callback: function (value) {
                  return yAxisTickCallback?.(value) ?? defaultYAxisTickCallback(value, locale);
                },
              },
            },
            y2: {
              alignToPixels: true,
              display: !configOptions?.hideAllScales,
              type: 'linear',
              position: 'right',
              grid: {
                color: 'transparent',
              },
              border: {
                display: false,
              },
              afterDataLimits: (axis) => {
                const yScale = axis.chart.scales['y'];
                if (!yScale) return;
                axis.min = yScale.min;
                axis.max = yScale.max;
              },
              ticks: {
                ...buildTicks('#969696', tickFontSize),
                callback: function (value) {
                  return yAxisTickCallback?.(value) ?? defaultYAxisTickCallback(value, locale);
                },
              },
            },
          },
          elements: {
            point: {
              radius: hasSingleLabel ? 2 : 0,
              hoverRadius: 4,
              hoverBorderWidth: 3,
              hoverBorderColor: '#fff',
            },
          },
        },
      };

      if (!chartRef.current) {
        chartRef.current = new Chart<'line'>(canvasContext, config);
        setTooltipContext({ chart: chartRef.current, tooltip: chartRef.current.tooltip! });

        chartRef.current.$showDataLabels = !!configOptions.showDataLabels;
        chartRef.current.$highlightMinMax = !!configOptions.highlightMinMax;
        chartRef.current.$minMaxValues = minMaxValuesRef.current;

        // Initial chart creation
        return;
      }

      chartRef.current.$showDataLabels = !!configOptions.showDataLabels;
      chartRef.current.$highlightMinMax = !!configOptions.highlightMinMax;
      chartRef.current.$minMaxValues = minMaxValuesRef.current;

      // If chart exists, update it
      updateChart(chartRef.current, config, containerRef.current);
      // Make sure to show the Live Chip on first render & update it with subsequent changes (data, locale, etc.)
      setTooltipContext({ chart: chartRef.current, tooltip: chartRef.current.tooltip! });
    }, [
      limitTicks,
      locale,
      tickFontSize,
      tooltipFn,
      chartData,
      configOptions?.stacked,
      yAxisTickCallback,
      configOptions?.hideAllScales,
      configOptions.showTrendLine,
      configOptions.showDataLabels,
      configOptions.highlightMinMax,
      datalabelsValueFormatter,
      translate,
    ]);

    return (
      <div ref={containerRef} className={classNames('relative', className)} style={wrapperStyle}>
        {isLoading && <Skeleton className='h-full rounded-24' />}
        <canvas
          className={classNames({ 'invisible absolute': isLoading })}
          id={id}
          data-testid={TEST_IDS.helpers.createChartCanvasId(id)}
          ref={(ref) => {
            canvasRef.current = ref;
          }}
        />
        {tooltipContext && !isLoading && tooltipFn && tooltipFn(tooltipContext)}
      </div>
    );
  },
);

function updateChart(chart: Chart<'line'>, config: ChartConfiguration<'line'>, containerElement: HTMLDivElement) {
  if (isNil(chart?.data?.datasets) || isNil(config?.data?.datasets)) return;

  // Resize chart
  const containerRect = containerElement.getBoundingClientRect();
  const containerHeight = containerRect.height;
  const containerWidth = containerRect.width;
  chart.resize(containerWidth, containerHeight);

  // Update chart options
  if (config.options) {
    chart.options = config.options;
  }

  // Update chart labels
  if (chart.data.labels) {
    chart.data.labels = config.data.labels;
  }

  if (chart.data.datasets.length !== config.data.datasets.length) {
    handleLengthChange(chart, config);
    chart.update();
    return;
  }

  for (let i = 0; i < config.data.datasets.length; i++) {
    const dataset = config.data.datasets[i];
    assign(chart.data.datasets[i], dataset);
  }

  // Re-draw chart
  chart.update();
}

function handleLengthChange(chart: Chart<'line'>, config: ChartConfiguration<'line'>) {
  let cfgDatasets = [...config.data.datasets];

  for (let i = 0; i < chart.data.datasets.length; i++) {
    const chartDataset = chart.data.datasets[i];

    const foundIndex = cfgDatasets.findIndex(
      (configDataset) =>
        configDataset.label === chartDataset.label && configDataset?.isCurrent === chartDataset?.isCurrent,
    );

    if (foundIndex > -1) {
      assign(chart.data.datasets[i], cfgDatasets[foundIndex]);
      cfgDatasets = cfgDatasets.filter((_, index) => index !== foundIndex);
    } else {
      chart.data.datasets = chart.data.datasets.filter((_, index) => index !== i);
      i--;
    }
  }

  chart.data.datasets = [...chart.data.datasets, ...cfgDatasets];
}
